import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Star, Heart, MapPin, Gift, ChevronRight,
  ChevronLeft, X, Sparkles, Lightbulb, Zap, Target,
  Clock, Award, RotateCcw,
} from "lucide-react";

/* ─────────────── FAKE OFFER CARD (reused in demos) ─────────────── */
const FakeCard = ({ highlights = {} }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
    <div className="flex gap-3">
      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-400 to-pink-500 flex items-center justify-center flex-shrink-0">
        <span className="text-white font-bold text-sm">T</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-sm font-semibold text-slate-800">Tarte Cosmetics</h3>
          <div className="flex items-center gap-1.5">
            <div className={`relative ${highlights.favorite ? "z-50" : ""}`}>
              <Heart className="w-4 h-4 text-slate-300" />
              {highlights.favorite && <PulsingDot />}
            </div>
            <span className="px-2 py-0.5 rounded-lg text-[10px] font-bold text-white bg-green-500">5 days</span>
          </div>
        </div>
        <p className="text-xs text-slate-500 mt-0.5">Up to 25% off</p>
        <div className="flex items-center flex-wrap gap-2 mt-2.5">
          <div className={`relative bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-lg ${highlights.ease ? "z-50 ring-2 ring-sky-400 ring-offset-1" : ""}`}>
            <div className="flex gap-0.5">
              {[1,2,3,4].map(i => <Star key={i} className="w-3 h-3 fill-amber-400 text-amber-400" />)}
              <Star className="w-3 h-3 text-slate-200" />
            </div>
            {highlights.ease && <ShimmerOverlay />}
          </div>
          <div className={`relative bg-green-50 border border-green-200 px-2 py-0.5 rounded-lg ${highlights.value ? "z-50 ring-2 ring-sky-400 ring-offset-1" : ""}`}>
            <span className="text-[10px] text-green-500">discount</span>
            <span className="text-xs font-bold text-green-700 ml-0.5">25% off</span>
            {highlights.value && <ShimmerOverlay />}
          </div>
          <div className={`relative bg-sky-50 border border-sky-200 px-2 py-0.5 rounded-lg ${highlights.window ? "z-50 ring-2 ring-sky-400 ring-offset-1" : ""}`}>
            <span className="text-[10px] font-semibold text-sky-700">Birthday only</span>
            {highlights.window && <ShimmerOverlay />}
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-1.5">
          <span className={`relative px-1.5 py-0.5 bg-fuchsia-50 text-fuchsia-600 border border-fuchsia-200 rounded text-[10px] font-semibold ${highlights.category ? "z-50 ring-2 ring-sky-400 ring-offset-1" : ""}`}>
            Cosmetics
            {highlights.category && <ShimmerOverlay />}
          </span>
          <span className="px-1.5 py-0.5 bg-violet-50 text-violet-600 border border-violet-200 rounded text-[10px] font-semibold">A.I. Sourced</span>
        </div>
      </div>
    </div>
  </div>
);

/* ─────────────── HELPERS ─────────────── */
const PulsingDot = () => (
  <span className="absolute -top-1 -right-1 w-2.5 h-2.5">
    <span className="absolute inset-0 rounded-full bg-sky-400 animate-ping opacity-75" />
    <span className="absolute inset-0 rounded-full bg-sky-500" />
  </span>
);

const ShimmerOverlay = () => (
  <div className="absolute inset-0 rounded-lg overflow-hidden pointer-events-none">
    <div className="absolute inset-0 -translate-x-full animate-[shimmer_7s_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent" />
  </div>
);

/* ═══════════════ PATTERN 1: COACH MARKS / SPOTLIGHT ═══════════════ */
const CoachMarksDemo = () => {
  const [step, setStep] = useState(-1);
  const steps = [
    { highlight: "ease", title: "Ease Rating", desc: "Tap the stars to see how easy this deal is to claim", pos: "top" },
    { highlight: "value", title: "Deal Value", desc: "Tap to learn how the value was estimated", pos: "top" },
    { highlight: "category", title: "Category Filter", desc: "Tap any category to instantly filter all deals", pos: "bottom" },
    { highlight: "favorite", title: "Save Favorites", desc: "Tap the heart to save deals you love", pos: "top" },
  ];
  const active = steps[step];

  return (
    <div className="relative">
      {step >= 0 && (
        <div className="absolute inset-0 bg-black/50 rounded-2xl z-40" onClick={() => setStep(s => s < steps.length - 1 ? s + 1 : -1)} />
      )}
      <FakeCard highlights={active ? { [active.highlight]: true } : {}} />
      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0, y: active.pos === "top" ? -10 : 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className={`absolute left-4 right-4 z-50 ${active.pos === "top" ? "top-2" : "bottom-2"}`}
          >
            <div className="bg-sky-500 text-white rounded-xl p-3 shadow-lg">
              <p className="text-xs font-bold">{active.title}</p>
              <p className="text-[11px] text-sky-100 mt-0.5">{active.desc}</p>
              <div className="flex items-center justify-between mt-2">
                <div className="flex gap-1">
                  {steps.map((_, i) => <div key={i} className={`w-1.5 h-1.5 rounded-full ${i === step ? "bg-white" : "bg-white/30"}`} />)}
                </div>
                <span className="text-[10px] text-sky-200">Tap to continue</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {step < 0 && (
        <button onClick={() => setStep(0)} className="mt-3 w-full py-2.5 bg-sky-500 text-white rounded-xl text-sm font-semibold" data-testid="coach-start-btn">
          Start Tour
        </button>
      )}
      {step >= steps.length && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-3 text-center">
          <p className="text-sm font-semibold text-green-600">Tour complete!</p>
          <p className="text-[11px] text-slate-400 mt-1">Tap the reset icon above to replay</p>
        </motion.div>
      )}
    </div>
  );
};

/* ═══════════════ PATTERN 2: PULSING DOT / SHIMMER ═══════════════ */
const PulsingHintDemo = () => {
  const [dismissed, setDismissed] = useState({});
  const [showInfo, setShowInfo] = useState(null);
  const allDismissed = Object.keys(dismissed).length >= 3;

  const pills = [
    { key: "ease", info: "5/5 Ease — Walk in, mention birthday. No app needed!", color: "bg-amber-50 border-amber-200" },
    { key: "value", info: "Estimated ~$50 based on typical gift set prices at Sephora", color: "bg-green-50 border-green-200" },
    { key: "window", info: "You have 14 days around your birthday to claim this deal", color: "bg-sky-50 border-sky-200" },
  ];

  const handleTap = (key, info) => {
    if (!dismissed[key]) {
      setShowInfo(info);
      setDismissed(d => ({ ...d, [key]: true }));
      setTimeout(() => setShowInfo(null), 3000);
    }
  };

  return (
    <div>
      <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm relative">
        <div className="flex gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-400 to-orange-500 flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold text-sm">S</span>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-slate-800">Sephora</h3>
            <p className="text-xs text-slate-500 mt-0.5">Birthday gift set</p>
            <div className="flex items-center flex-wrap gap-2 mt-2.5">
              <div
                className={`relative ${pills[0].color} border px-2 py-0.5 rounded-lg cursor-pointer active:scale-95 transition-transform`}
                onClick={() => handleTap("ease", pills[0].info)}
              >
                <div className="flex gap-0.5">
                  {[1,2,3,4,5].map(i => <Star key={i} className="w-3 h-3 fill-amber-400 text-amber-400" />)}
                </div>
                {!dismissed.ease && <ShimmerOverlay />}
                {!dismissed.ease && <PulsingDot />}
              </div>
              <div
                className={`relative ${pills[1].color} border px-2 py-0.5 rounded-lg cursor-pointer active:scale-95 transition-transform`}
                onClick={() => handleTap("value", pills[1].info)}
              >
                <span className="text-[10px] text-green-500">worth</span>
                <span className="text-xs font-bold text-green-700 ml-0.5">~$50</span>
                {!dismissed.value && <ShimmerOverlay />}
                {!dismissed.value && <PulsingDot />}
              </div>
              <div
                className={`relative ${pills[2].color} border px-2 py-0.5 rounded-lg cursor-pointer active:scale-95 transition-transform`}
                onClick={() => handleTap("window", pills[2].info)}
              >
                <span className="text-[10px] font-semibold text-sky-700">14d window</span>
                {!dismissed.window && <ShimmerOverlay />}
                {!dismissed.window && <PulsingDot />}
              </div>
            </div>
          </div>
        </div>
        <AnimatePresence>
          {showInfo && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              className="mt-3 bg-slate-800 text-white rounded-xl px-3 py-2.5 text-xs font-medium shadow-lg"
            >
              {showInfo}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <p className="text-[11px] text-slate-400 text-center mt-2">
        {allDismissed
          ? "All discovered! The glow only appears once per feature. Tap reset to replay."
          : `Tap a glowing pill to discover what it does (${3 - Object.keys(dismissed).length} left)`}
      </p>
    </div>
  );
};

/* ═══════════════ PATTERN 3: CONTEXTUAL TOOLTIPS ═══════════════ */
const ContextualTooltipDemo = () => {
  const [activeTooltip, setActiveTooltip] = useState("ease");

  return (
    <div>
      <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm relative">
        <div className="flex gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-400 to-green-500 flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold text-sm">S</span>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-slate-800">Starbucks</h3>
            <p className="text-xs text-slate-500 mt-0.5">Free birthday drink</p>
            <div className="flex items-center flex-wrap gap-2 mt-2.5">
              <div className="relative">
                <div className="bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-lg cursor-pointer" onClick={() => setActiveTooltip(t => t === "ease" ? null : "ease")}>
                  <div className="flex gap-0.5">
                    {[1,2,3].map(i => <Star key={i} className="w-3 h-3 fill-amber-400 text-amber-400" />)}
                    {[4,5].map(i => <Star key={i} className="w-3 h-3 text-slate-200" />)}
                  </div>
                </div>
                <AnimatePresence>
                  {activeTooltip === "ease" && (
                    <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 5 }} className="absolute top-full left-0 mt-1.5 z-50 w-48">
                      <div className="bg-slate-800 text-white rounded-lg p-2.5 shadow-lg relative">
                        <div className="absolute -top-1 left-4 w-2 h-2 bg-slate-800 rotate-45" />
                        <p className="text-[11px] font-semibold">Ease of Claim: 3/5</p>
                        <p className="text-[10px] text-slate-300 mt-0.5">App download or loyalty signup required</p>
                        <button onClick={(e) => { e.stopPropagation(); setActiveTooltip(null); }} className="text-[9px] text-sky-400 mt-1">Got it</button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              <div className="relative">
                <div className="bg-green-50 border border-green-200 px-2 py-0.5 rounded-lg cursor-pointer" onClick={() => setActiveTooltip(t => t === "value" ? null : "value")}>
                  <span className="text-[10px] text-green-500">worth</span>
                  <span className="text-xs font-bold text-green-700 ml-0.5">~$6</span>
                </div>
                <AnimatePresence>
                  {activeTooltip === "value" && (
                    <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 5 }} className="absolute top-full left-0 mt-1.5 z-50 w-52">
                      <div className="bg-slate-800 text-white rounded-lg p-2.5 shadow-lg relative">
                        <div className="absolute -top-1 left-4 w-2 h-2 bg-slate-800 rotate-45" />
                        <p className="text-[11px] font-semibold">Estimated Value</p>
                        <p className="text-[10px] text-slate-300 mt-0.5">Based on typical drink prices at Starbucks</p>
                        <button onClick={(e) => { e.stopPropagation(); setActiveTooltip(null); }} className="text-[9px] text-sky-400 mt-1">Got it</button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </div>
      <p className="text-[11px] text-slate-400 text-center mt-2">Tap pills to toggle dark tooltips</p>
    </div>
  );
};

/* ═══════════════ PATTERN 4: WELCOME CAROUSEL ═══════════════ */
const WelcomeCarouselDemo = () => {
  const [show, setShow] = useState(false);
  const [slide, setSlide] = useState(0);
  const slides = [
    { icon: Gift, color: "from-sky-400 to-sky-500", title: "382 Birthday Deals", desc: "Browse deals from your favorite brands — all free on your birthday." },
    { icon: Star, color: "from-amber-400 to-amber-500", title: "Tap Any Badge", desc: "Tap pills on deal cards to see ratings, values, and filter by category." },
    { icon: Heart, color: "from-red-400 to-red-500", title: "Save & Track", desc: "Favorite the deals you love and track which ones you've claimed." },
    { icon: Zap, color: "from-green-400 to-green-500", title: "Earn Points", desc: "Verify deals to earn 15 pts each, climb the leaderboard, and unlock badges." },
  ];

  return (
    <div>
      <button onClick={() => { setShow(true); setSlide(0); }} className="w-full py-2.5 bg-sky-500 text-white rounded-xl text-sm font-semibold" data-testid="carousel-start-btn">
        Show Welcome Carousel
      </button>
      <AnimatePresence>
        {show && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/60 z-[200] flex items-center justify-center p-6" onClick={() => setShow(false)}>
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="bg-white rounded-3xl w-full max-w-sm overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
              <div className={`bg-gradient-to-br ${slides[slide].color} p-8 text-center text-white relative`}>
                <button onClick={() => setShow(false)} className="absolute top-3 right-3 p-1 rounded-full bg-white/20"><X className="w-4 h-4" /></button>
                {(() => { const Icon = slides[slide].icon; return <Icon className="w-14 h-14 mx-auto mb-3 opacity-90" />; })()}
                <h3 className="text-lg font-bold">{slides[slide].title}</h3>
                <p className="text-sm text-white/80 mt-1">{slides[slide].desc}</p>
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex gap-1.5">
                    {slides.map((_, i) => <div key={i} className={`w-2 h-2 rounded-full transition-colors ${i === slide ? "bg-sky-500" : "bg-slate-200"}`} />)}
                  </div>
                  <div className="flex gap-2">
                    {slide > 0 && <button onClick={() => setSlide(s => s - 1)} className="p-2 rounded-full bg-slate-100"><ChevronLeft className="w-4 h-4 text-slate-600" /></button>}
                    {slide < slides.length - 1 ? (
                      <button onClick={() => setSlide(s => s + 1)} className="px-4 py-2 bg-sky-500 text-white rounded-xl text-sm font-semibold flex items-center gap-1">
                        Next <ChevronRight className="w-4 h-4" />
                      </button>
                    ) : (
                      <button onClick={() => setShow(false)} className="px-4 py-2 bg-green-500 text-white rounded-xl text-sm font-semibold">
                        Let's Go!
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ═══════════════ PATTERN 5: PROGRESSIVE DISCLOSURE ═══════════════ */
const ProgressiveDisclosureDemo = () => {
  const [stage, setStage] = useState(0);
  const milestones = [
    { trigger: "First visit", message: "Welcome! Browse 382 birthday deals from brands you love.", icon: Gift, color: "bg-sky-50 border-sky-200 text-sky-700" },
    { trigger: "First favorite", message: "Nice save! Did you know you can tap any pill badge for quick info?", icon: Lightbulb, color: "bg-amber-50 border-amber-200 text-amber-700" },
    { trigger: "First claim", message: "You claimed your first deal! Verify it to earn 15 pts and help others.", icon: Target, color: "bg-green-50 border-green-200 text-green-700" },
    { trigger: "5 claims", message: "You're on fire! Check your analytics to see your savings breakdown.", icon: Zap, color: "bg-purple-50 border-purple-200 text-purple-700" },
  ];

  return (
    <div className="space-y-3">
      <AnimatePresence mode="wait">
        <motion.div key={stage} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className={`rounded-xl border p-3.5 ${milestones[stage].color}`}>
          <div className="flex items-start gap-2.5">
            {(() => { const Icon = milestones[stage].icon; return <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" />; })()}
            <div className="flex-1">
              <p className="text-[10px] font-bold uppercase tracking-wider opacity-60">{milestones[stage].trigger}</p>
              <p className="text-sm font-medium mt-0.5">{milestones[stage].message}</p>
            </div>
            <button onClick={() => setStage(s => Math.min(s + 1, milestones.length - 1))} className="p-1 rounded-full opacity-40 hover:opacity-70">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </motion.div>
      </AnimatePresence>
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <motion.div animate={{ width: `${((stage + 1) / milestones.length) * 100}%` }} className="h-full bg-gradient-to-r from-sky-400 to-sky-500 rounded-full" />
        </div>
        <span className="text-[10px] text-slate-400 font-medium">{stage + 1}/{milestones.length}</span>
      </div>
      {stage < milestones.length - 1 && (
        <button onClick={() => setStage(s => Math.min(s + 1, milestones.length - 1))} className="text-xs text-sky-500 font-medium">Simulate next milestone</button>
      )}
      {stage >= milestones.length - 1 && (
        <p className="text-[11px] text-slate-400">All milestones reached! Tap reset icon above to replay.</p>
      )}
    </div>
  );
};

/* ═══════════════ PATTERN 6: INLINE TIP BANNER ═══════════════ */
const InlineBannerDemo = () => {
  const [tips, setTips] = useState([
    { id: 1, text: "Tap any badge on a deal card for quick details", icon: Sparkles, color: "bg-sky-50 border-sky-200" },
    { id: 2, text: "Tap a category pill to filter all deals instantly", icon: Target, color: "bg-teal-50 border-teal-200" },
    { id: 3, text: "Verify deals you've claimed to earn 15 pts each", icon: Award, color: "bg-amber-50 border-amber-200" },
  ]);

  return (
    <div className="space-y-2">
      <AnimatePresence>
        {tips.map(tip => {
          const Icon = tip.icon;
          return (
            <motion.div key={tip.id} initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0, marginBottom: 0 }} className={`${tip.color} border rounded-xl px-3 py-2.5 flex items-center gap-2.5`}>
              <Icon className="w-4 h-4 text-sky-500 flex-shrink-0" />
              <p className="text-xs font-medium text-slate-700 flex-1">{tip.text}</p>
              <button onClick={() => setTips(t => t.filter(x => x.id !== tip.id))} className="p-1 rounded-full hover:bg-white/50">
                <X className="w-3 h-3 text-slate-400" />
              </button>
            </motion.div>
          );
        })}
      </AnimatePresence>
      {tips.length === 0 && (
        <div className="text-center py-3">
          <p className="text-xs text-slate-400">All tips dismissed! Tap reset icon above to replay.</p>
        </div>
      )}
      <FakeCard />
    </div>
  );
};

/* ═══════════════ SECTION WRAPPER ═══════════════ */
const DemoSection = ({ number, title, subtitle, usedBy, onReset, children }) => (
  <div className="bg-white rounded-2xl shadow-md overflow-hidden" data-testid={`demo-section-${number}`}>
    <div className="bg-gradient-to-r from-slate-700 to-slate-800 px-4 py-3">
      <div className="flex items-center gap-2.5">
        <span className="w-7 h-7 rounded-lg bg-white/15 flex items-center justify-center text-sm font-bold text-white">{number}</span>
        <div className="flex-1">
          <h3 className="text-sm font-bold text-white">{title}</h3>
          <p className="text-[10px] text-slate-400">{subtitle}</p>
        </div>
        {onReset && (
          <button onClick={onReset} className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition" data-testid={`reset-demo-${number}`}>
            <RotateCcw className="w-3.5 h-3.5 text-white" />
          </button>
        )}
      </div>
      <p className="text-[10px] text-sky-300 mt-1.5">Used by: {usedBy}</p>
    </div>
    <div className="p-4">{children}</div>
  </div>
);

/* ═══════════════ MAIN PAGE ═══════════════ */
export default function OnboardingDemoPage() {
  const navigate = useNavigate();
  const [resetKeys, setResetKeys] = useState({ 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 });

  const resetOne = (n) => setResetKeys(k => ({ ...k, [n]: k[n] + 1 }));
  const resetAll = () => setResetKeys(k => Object.fromEntries(Object.keys(k).map(n => [n, k[n] + 1])));

  return (
    <div className="min-h-screen bg-slate-50 pb-8" data-testid="onboarding-demo-page">
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 text-white p-5 pt-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/profile")} className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition" data-testid="demo-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold">Onboarding Patterns</h1>
            <p className="text-xs text-slate-400">Tap to try each one — reset anytime</p>
          </div>
        </div>
        <div className="flex gap-2 mt-3">
          <button onClick={resetAll} className="flex-1 py-2 bg-white/10 hover:bg-white/20 rounded-xl text-xs font-semibold transition flex items-center justify-center gap-1.5" data-testid="reset-all-btn">
            <RotateCcw className="w-3.5 h-3.5" /> Reset All Demos
          </button>
          <button onClick={() => navigate("/profile")} className="flex-1 py-2 bg-red-500/80 hover:bg-red-500 rounded-xl text-xs font-semibold transition flex items-center justify-center gap-1.5" data-testid="exit-demo-btn">
            <X className="w-3.5 h-3.5" /> Exit Demo Mode
          </button>
        </div>
      </div>

      <div className="px-4 mt-4 space-y-4">
        <DemoSection number="1" title="Coach Marks / Spotlight Tour" subtitle="Step-by-step overlay highlighting one element at a time" usedBy="Slack, Airbnb, Duolingo" onReset={() => resetOne(1)}>
          <CoachMarksDemo key={resetKeys[1]} />
        </DemoSection>

        <DemoSection number="2" title="Pulsing Dot / Shimmer Hint" subtitle="Subtle glowing dots + shimmer on interactive elements" usedBy="Instagram, Spotify" onReset={() => resetOne(2)}>
          <PulsingHintDemo key={resetKeys[2]} />
        </DemoSection>

        <DemoSection number="3" title="Contextual Tooltips" subtitle="Dark floating tips that appear near tapped elements" usedBy="Notion, Figma" onReset={() => resetOne(3)}>
          <ContextualTooltipDemo key={resetKeys[3]} />
        </DemoSection>

        <DemoSection number="4" title="Welcome Carousel" subtitle="Swipeable intro screens on first login" usedBy="TikTok, Uber" onReset={() => resetOne(4)}>
          <WelcomeCarouselDemo key={resetKeys[4]} />
        </DemoSection>

        <DemoSection number="5" title="Progressive Disclosure" subtitle="Reveal tips as user hits milestones" usedBy="Duolingo, LinkedIn" onReset={() => resetOne(5)}>
          <ProgressiveDisclosureDemo key={resetKeys[5]} />
        </DemoSection>

        <DemoSection number="6" title="Inline Tip Banners" subtitle="Dismissible tip cards above content" usedBy="Google apps, Reddit" onReset={() => resetOne(6)}>
          <InlineBannerDemo key={resetKeys[6]} />
        </DemoSection>

        {/* Live hints tester */}
        <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-4 shadow-md" data-testid="live-hints-section">
          <h3 className="text-sm font-bold text-white flex items-center gap-2"><Sparkles className="w-4 h-4" /> Live: Pulsing Dot Hints</h3>
          <p className="text-xs text-emerald-100 mt-1">Pattern #2 is active on real offer cards. Reset to simulate a new user and see the hints in action.</p>
          <button
            onClick={() => {
              localStorage.removeItem("onboarding_hints_dismissed");
              window.dispatchEvent(new Event("onboarding-hint-changed"));
              navigate("/");
            }}
            className="mt-3 w-full py-2.5 bg-white text-emerald-700 rounded-xl text-sm font-bold active:scale-95 transition-transform"
            data-testid="reset-live-hints-btn"
          >
            Reset Hints & Go to Homepage
          </button>
        </div>
      </div>
    </div>
  );
}
